import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/90 backdrop-blur-md border-b border-background-200/70">
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
              superbuyspread<span className="text-accent-500">.pics</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onMouseEnter={() => setDropdownOpen(true)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <div className="py-2 max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/article/superbuy-sheet-complete-guide" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
              Guides
            </Link>
            <Link to="/article/superbuy-sheet-complete-guide" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
              Tutorial
            </Link>
            <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
              FAQ
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 max-h-[85vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Categories section */}
            <div>
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="flex items-center justify-between w-full py-2"
              >
                <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Product Categories</span>
                <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCategoryOpen && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-3 py-3 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors"
                    >
                      <span className="w-8 h-8 flex items-center justify-center bg-background-50 rounded-md flex-shrink-0">
                        <i className={`${cat.icon} text-base text-secondary-600`}></i>
                      </span>
                      <span className="text-xs leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Quick links */}
            <div className="border-t border-background-200/70 pt-4">
              <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Resources</span>
              <div className="mt-2 space-y-1">
                <Link to="/article/superbuy-sheet-complete-guide" className="flex items-center gap-2 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 transition-colors">
                  <i className="ri-book-open-line text-secondary-500"></i>
                  Guides
                </Link>
                <Link to="/article/superbuy-sheet-complete-guide" className="flex items-center gap-2 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 transition-colors">
                  <i className="ri-graduation-cap-line text-secondary-500"></i>
                  Tutorial
                </Link>
                <Link to="/" className="flex items-center gap-2 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 transition-colors">
                  <i className="ri-questionnaire-line text-secondary-500"></i>
                  FAQ
                </Link>
                <Link to="/article/superbuy-sheet-safety-tips" className="flex items-center gap-2 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 transition-colors">
                  <i className="ri-shield-check-line text-secondary-500"></i>
                  Safety Guide
                </Link>
              </div>
            </div>

            {/* CTA buttons */}
            <div className="border-t border-background-200/70 pt-4 space-y-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors"
              >
                superbuy sheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-5 py-3.5 border border-foreground-300 text-foreground-700 text-sm font-semibold rounded-full hover:bg-foreground-100 transition-colors"
              >
                buy it now
                <i className="ri-shopping-cart-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}